import { useEffect, useState } from 'react'

/**
 * On-device viewport diagnostics for the mobile keyboard work. Activated only
 * with `?vvdebug=1` (or localStorage 'cc-vv-debug' = '1') — invisible
 * otherwise. Shows the RAW browser numbers next to the viewport store's view
 * of them plus the shell/feed geometry, so a single screenshot with the
 * keyboard open tells us exactly what iOS is doing instead of guessing:
 * whether the pan is document scroll or a visual-viewport pan, whether the
 * shell actually resized, and where the feed's scroll ended up.
 */

function viewportDebugEnabled(): boolean {
  try {
    return (
      new URLSearchParams(window.location.search).has('vvdebug')
      || window.localStorage.getItem('cc-vv-debug') === '1'
    )
  } catch {
    return false
  }
}

interface Sample {
  now: string
  innerH: number
  innerW: number
  scrollY: number
  vvH: number | null
  vvTop: number | null
  vvScale: number | null
  vvResizes: number
  vvScrolls: number
  rootTop: number | null
  rootH: number | null
  rootTransform: string
  feedTop: number | null
  feedScrollTop: number | null
  feedScrollH: number | null
  feedClientH: number | null
  active: string
  standalone: boolean
}

let vvResizes = 0
let vvScrolls = 0

function takeSample(): Sample {
  const vv = window.visualViewport
  const root = document.querySelector('[data-vv-root]') as HTMLElement | null
  const feed = document.querySelector('[data-vv-feed]') as HTMLElement | null
  const rootRect = root?.getBoundingClientRect()
  const feedRect = feed?.getBoundingClientRect()
  const active = document.activeElement
  return {
    now: new Date().toISOString().slice(11, 23),
    innerH: window.innerHeight,
    innerW: window.innerWidth,
    scrollY: Math.round(window.scrollY),
    vvH: vv ? Math.round(vv.height) : null,
    vvTop: vv ? Math.round(vv.offsetTop) : null,
    vvScale: vv ? Math.round((vv.scale || 1) * 100) / 100 : null,
    vvResizes,
    vvScrolls,
    rootTop: rootRect ? Math.round(rootRect.top) : null,
    rootH: rootRect ? Math.round(rootRect.height) : null,
    rootTransform: root ? (root.style.transform || 'none') : 'n/a',
    feedTop: feedRect ? Math.round(feedRect.top) : null,
    feedScrollTop: feed ? Math.round(feed.scrollTop) : null,
    feedScrollH: feed ? Math.round(feed.scrollHeight) : null,
    feedClientH: feed ? Math.round(feed.clientHeight) : null,
    active: active ? `${active.tagName.toLowerCase()}${active === document.body ? '(body)' : ''}` : 'none',
    standalone:
      (!!window.matchMedia && window.matchMedia('(display-mode: standalone)').matches)
      || (window.navigator as Navigator & { standalone?: boolean }).standalone === true,
  }
}

export default function ViewportDebugOverlay() {
  const [sample, setSample] = useState<Sample | null>(null)
  const enabled = viewportDebugEnabled()

  useEffect(() => {
    if (!enabled) return
    const vv = window.visualViewport
    const onResize = () => { vvResizes += 1 }
    const onScroll = () => { vvScrolls += 1 }
    vv?.addEventListener('resize', onResize)
    vv?.addEventListener('scroll', onScroll)

    // rAF-driven ~4Hz sampler (not tied to the viewport store — the overlay
    // must keep reporting raw values even if the store misbehaves).
    let raf = 0
    let last = 0
    const tick = (t: number) => {
      if (t - last > 250) {
        last = t
        setSample(takeSample())
      }
      raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)

    return () => {
      cancelAnimationFrame(raf)
      vv?.removeEventListener('resize', onResize)
      vv?.removeEventListener('scroll', onScroll)
    }
  }, [enabled])

  if (!enabled || !sample) return null
  const s = sample
  return (
    <div
      className="fixed left-1 top-1 z-[9999] pointer-events-none rounded bg-black/80 px-1.5 py-1 font-mono text-[9px] leading-[1.35] text-green-300"
      style={{ maxWidth: '60vw' }}
    >
      <div>{s.now} {s.standalone ? 'PWA' : 'tab'} {s.innerW}w</div>
      <div>innerH {s.innerH} scrollY {s.scrollY}</div>
      <div>vv h{s.vvH} top{s.vvTop} x{s.vvScale} r{s.vvResizes}/s{s.vvScrolls}</div>
      <div>root top{s.rootTop} h{s.rootH} tf:{s.rootTransform}</div>
      <div>feed top{s.feedTop} st{s.feedScrollTop} sh{s.feedScrollH} ch{s.feedClientH}</div>
      <div>focus:{s.active}</div>
    </div>
  )
}
